import { useCallback, useEffect, useMemo, useState, useRef } from "react";
import { APP_BASE_URL, GET_ALL_ASSETS, GET_MAINTENACE_ASSET, GET_MAINTENANCE_SCHEDULE } from "../../Common/EndPoints";
import { useSelector } from "react-redux";
import { selectedCustomersSelector } from "../../features/authentication/auth-slice";
import axios from "axios";
import toast from "react-hot-toast";
import "./maintenanceschedulewidget.scss";
import { Chart } from 'react-chartjs-2';
import { Chart as ChartJS, ChartOptions, ChartData } from "chart.js";
import ChartDataLabels from "chartjs-plugin-datalabels";
ChartJS.register(ChartDataLabels);

// {
//     "customerName": "DER",
//     "notStartedCount": 44, // TO_BE_STARTED
//     "doneCount": 0, // ONTIME_COMPLETED
//     "dueCount": 0, // PENDING_OVERDUE
//     "overdueCount": 0 // OVERDUE_COMPLETED
// }

enum MaintenanceScheduleEnum {
    "To be started" = "TO_BE_STARTED",
    "Ontime completed" = "ONTIME_COMPLETED",
    "Pending (Overdue)" = "PENDING_OVERDUE",
    "Overdue completed" = "OVERDUE_COMPLETED",
}

const colors = {
    [MaintenanceScheduleEnum["To be started"]]: 'rgba(54,162,235,0.8)',
    [MaintenanceScheduleEnum["Ontime completed"]]: 'rgba(75, 192, 87, 0.8)',
    [MaintenanceScheduleEnum["Overdue completed"]]: 'rgba(255,159,64,0.8)',
    [MaintenanceScheduleEnum["Pending (Overdue)"]]: 'rgba(255, 64, 64, 0.8)',
}

const getMainChartData = (assetData: any) => {
    const data = assetData || [];
    const chartData: ChartData<"bar"> = {
        labels: data.map((item: any) => item.customerName),
        datasets: [
            {
                label: 'To be started',
                data: data.map((item: any) => item?.due || 0),
                backgroundColor: colors[MaintenanceScheduleEnum["To be started"]],
                barThickness: "flex"
            },
            {
                label: 'Ontime completed',
                data: data.map((item: any) => item?.ontime || 0),
                backgroundColor: colors[MaintenanceScheduleEnum["Ontime completed"]],
                barThickness: "flex"
            },
            {
                label: 'Overdue completed',
                data: data.map((item: any) => item?.overdueDone || 0),
                backgroundColor: colors[MaintenanceScheduleEnum["Overdue completed"]],
                barThickness: "flex"
            },
            {
                label: 'Pending (Overdue)',
                data: data.map((item: any) => item?.overdue || 0),
                backgroundColor: colors[MaintenanceScheduleEnum["Pending (Overdue)"]],
                barThickness: "flex"
            }
        ]
    }
    return chartData;
}

const getMaintenanceScheduleTypeLabel = (maintenanceScheduleType: string) => {
    switch (maintenanceScheduleType) {
        case MaintenanceScheduleEnum["To be started"]:
            return "To be started";
        case MaintenanceScheduleEnum["Ontime completed"]:
            return "Ontime completed";
        case MaintenanceScheduleEnum["Overdue completed"]:
            return "Overdue completed";
        case MaintenanceScheduleEnum["Pending (Overdue)"]:
            return "Pending (Overdue)";
        default:
            return "";
    }
}


const getChildChartData = (maintenanceData: any, maintenanceScheduleType: string) => {
    let data = []
    switch (maintenanceScheduleType) {
        case MaintenanceScheduleEnum["To be started"]:
            data = maintenanceData.map((item: any) => item?.due || 0)
            break;
        case MaintenanceScheduleEnum["Ontime completed"]:
            data = maintenanceData.map((item: any) => item?.ontime || 0)
            break;
        case MaintenanceScheduleEnum["Overdue completed"]:
            data = maintenanceData.map((item: any) => item?.overdueDone || 0)
            break;
        case MaintenanceScheduleEnum["Pending (Overdue)"]:
            data = maintenanceData.map((item: any) => item?.overdue || 0)
            break;
        default:
            data = []
    }

    const chartData: ChartData<"bar"> = {
        labels: maintenanceData.map((item: any) => item.deviceType),
        datasets: [
            {
                label: getMaintenanceScheduleTypeLabel(maintenanceScheduleType),
                data: data,
                backgroundColor: colors[maintenanceScheduleType as keyof typeof colors],
                barThickness: "flex",
            },

        ]
    }
    return chartData;
}


type MaintenanceScheduleChartAttribute = {
    maintenanceScheduleType: "TO_BE_STARTED" | "ONTIME_COMPLETED" | "OVERDUE_COMPLETED" | "PENDING_OVERDUE" | null;
    station: string | null;
}

const initialMaintenanceScheduleChartAttribute: MaintenanceScheduleChartAttribute = {
    maintenanceScheduleType: null,
    station: null
}


const MaintenanceScheduleWidget = ({ selectedStations }: { selectedStations: string }) => {
    const selectedCustomersList = useSelector(selectedCustomersSelector);
    const permittedCustomerIds = useMemo(() => {
        const _list = selectedCustomersList;
        if (!_list) return [];
        return _list.map((customer) => customer.customerId);
    }, [selectedCustomersList]);
    const selectedCustomersListByProp = useMemo(() => {
        return selectedStations.split(",")
    }, [selectedStations]);
    const singleCustomer = selectedCustomersListByProp.length === 1 ? selectedCustomersList?.find((customer) => customer.customerId === selectedCustomersListByProp[0]) : null;
    const [maintenanceScheduleData, setMaintenanceScheduleData] = useState<any[]>([]);
    const mainChartData = useMemo(() => {
        return getMainChartData(maintenanceScheduleData);
    }, [maintenanceScheduleData])
    const [maintenanceScheduleStats, setMaintenanceScheduleStats] = useState({
        toBeStarted: 0,
        ontimeCompleted: 0,
        overdueCompleted: 0,
        pending: 0,
    })
    const [maintenanceScheduleChartAttribute, setMaintenanceScheduleChartAttribute] = useState<MaintenanceScheduleChartAttribute>(initialMaintenanceScheduleChartAttribute);
    const [maintenanceScheduleForStation, setMaintenanceScheduleForStation] = useState<any>();
    const childChartRef = useRef<any>(null);

    useEffect(() => {
        if(selectedCustomersListByProp.length > 0) {
            getMaintenanceScheduleData(selectedCustomersListByProp, "", "");
            setMaintenanceScheduleForStation(null);
            setMaintenanceScheduleChartAttribute(initialMaintenanceScheduleChartAttribute);
        }
    }, [selectedCustomersListByProp])

    const handleChartClick = async (_event: any, elements: any, chart: any) => {
        if (elements.length > 0) {
            const stationLabel = chart.tooltip?.dataPoints[0]?.label;
            const dataPointLabel = chart.tooltip?.dataPoints[0]?.dataset?.label;

            const stationCustomerId = selectedCustomersList.find((customer) =>
                customer.customerName === stationLabel
            )?.customerId;
            // console.log("dataPointLabel", dataPointLabel);

            if (stationCustomerId) {
                await getMaintenanceScheduleData([stationCustomerId], stationLabel, dataPointLabel);
                if (childChartRef.current) {
                    childChartRef.current.scrollIntoView({ behavior: 'smooth' });
                }
            }
        }
    };

    const mainChartOptions = useMemo(() => {
        const getMaxValue = () => {
            let y_max = 0;
            const values = (maintenanceScheduleData || []).map((item: any) => (
                [item?.due || 0, item?.ontime || 0, item?.overdueDone || 0, item?.overdue || 0]
            ));

            const maxValue = Math.max(...values.flat(), 0);
            y_max = maxValue * 1.2;
            return y_max;
        }

        const y_max = getMaxValue();

        const options: ChartOptions<"bar"> = {
            responsive: true,
            maintainAspectRatio: false,
            onClick: (event, elements, chart) => {
                handleChartClick(event, elements, chart)
            },
            datasets: {
                bar: {
                    barPercentage: 0.5,
                    borderWidth: 2,
                    borderColor: 'rgba(255, 255, 255)',
                }
            },
            plugins: {
                legend: { position: 'top' },
                datalabels: {
                    anchor: 'end',
                    align: 'top',
                    font: { weight: 'bold' },
                    formatter: v => v?.toLocaleString?.()
                }
            },
            scales: {
                y: {
                    beginAtZero: true,
                    ticks: { callback: v => v.toLocaleString() },
                    suggestedMax: y_max,
                },
            }
        };

        return options;
    }, [maintenanceScheduleData])



    useEffect(() => {
        if (permittedCustomerIds.length > 0) {
            getMaintenanceScheduleData(permittedCustomerIds, "", "");
            setMaintenanceScheduleForStation(null);
            setMaintenanceScheduleChartAttribute(initialMaintenanceScheduleChartAttribute);
        }
    }, [permittedCustomerIds]);


    const getMaintenanceScheduleData = async (customerIds: string[], stationLabel: string, dataPointLabel?: string) => {
        try {
            const url = `${APP_BASE_URL}${GET_MAINTENANCE_SCHEDULE}`;
            const params = {
                customerIds: customerIds.toString()
            }
            const res = await axios.get(url, { params });
            const data = res?.data?.data || [];
            if(singleCustomer && !stationLabel){
                const dataPacket = {
                    customerName: singleCustomer.customerName,
                    ontime: data.reduce((acc: number, curr: any) => acc + (curr?.ontime || 0), 0) || 0,
                    overdue: data.reduce((acc: number, curr: any) => acc + (curr?.overdue || 0), 0) || 0,
                    due: data.reduce((acc: number, curr: any) => acc + (curr?.due || 0), 0) || 0,
                    overdueDone: data.reduce((acc: number, curr: any) => acc + (curr?.overdueDone || 0), 0) || 0
                }
                setMaintenanceScheduleData([dataPacket]);
                setMaintenanceScheduleStats({
                    toBeStarted: dataPacket?.due || 0,
                    ontimeCompleted: dataPacket?.ontime || 0,
                    overdueCompleted: dataPacket?.overdueDone || 0,
                    pending: dataPacket?.overdue || 0
                });
                return;
            }
            if (stationLabel) {
                // Set data for single station view
                // setAssetForStation(data);
                setMaintenanceScheduleForStation(data);
                const maintenanceScheduleType = MaintenanceScheduleEnum[dataPointLabel as keyof typeof MaintenanceScheduleEnum] || null;

                setMaintenanceScheduleChartAttribute({
                    maintenanceScheduleType: maintenanceScheduleType,
                    station: stationLabel
                });
            } else {
                // clear child chart data
                setMaintenanceScheduleForStation(null);
                setMaintenanceScheduleChartAttribute(initialMaintenanceScheduleChartAttribute);
                // set main chart data
                // setAssetStats({
                //     allAssets: data.reduce((acc: number, curr: any) => acc + curr.allDevices, 0) || 0,
                //     maintenanceStarted: data.reduce((acc: number, curr: any) => acc + curr.maintenanceStarted, 0) || 0
                // });
                // setAssetData(data);
                setMaintenanceScheduleData(data);
                setMaintenanceScheduleStats({
                    toBeStarted: data.reduce((acc: number, curr: any) => acc + (curr?.due || 0), 0) || 0,
                    ontimeCompleted: data.reduce((acc: number, curr: any) => acc + (curr?.ontime || 0), 0) || 0,
                    overdueCompleted: data.reduce((acc: number, curr: any) => acc + (curr?.overdueDone || 0), 0) || 0,
                    pending: data.reduce((acc: number, curr: any) => acc + (curr?.overdue || 0), 0) || 0
                });
            }

        } catch (error: any) {
            if (axios.isAxiosError(error)) {
                toast.error(error?.response?.data?.message || "Something went wrong");
            } else {
                toast.error(error?.message || "Something went wrong");
            }
            console.log("MAINTENANCE_SCHEDULE_DATA_ERROR", error);
        }
    }

    const childChartOptions = useMemo(() => {
        let y_max = 0;
        if(maintenanceScheduleChartAttribute.maintenanceScheduleType === MaintenanceScheduleEnum["To be started"]) {
            y_max = Math.max(...(maintenanceScheduleForStation || []).map((item:any)=>item?.due || 0))
        }else if(maintenanceScheduleChartAttribute.maintenanceScheduleType === MaintenanceScheduleEnum["Ontime completed"]) {
            y_max = Math.max(...(maintenanceScheduleForStation || []).map((item:any)=>item?.ontime || 0))
        }else if(maintenanceScheduleChartAttribute.maintenanceScheduleType === MaintenanceScheduleEnum["Overdue completed"]) {
            y_max = Math.max(...(maintenanceScheduleForStation || []).map((item:any)=>item?.overdueDone || 0))
        }else if(maintenanceScheduleChartAttribute.maintenanceScheduleType === MaintenanceScheduleEnum["Pending (Overdue)"]) {
            y_max = Math.max(...(maintenanceScheduleForStation || []).map((item:any)=>item?.overdue || 0))
        }

        y_max *= 1.1;

        const childChartOptions: ChartOptions<"bar"> = {
            responsive: true,
            maintainAspectRatio: false,
            layout: {
                padding: {
                    left: 50,
                    right: 50,
                    top: 50,
                    bottom: 50
                }
            },
            plugins: {
                legend: { display: false },
                datalabels: {
                    anchor: 'end',
                    align: 'end',
                    font: {
                        weight: 'bold',
                        size: 12
                    },
                    formatter: v => v.toLocaleString(),
                    clamp: true,
                    clip: true,
                    textAlign: 'center',
                }
            },
            scales: {
                y: {
                    beginAtZero: true,
                    suggestedMax: y_max,
                    ticks: { callback: v => v.toLocaleString() },
                },
            }
        };

        return childChartOptions;
    }, [maintenanceScheduleForStation, maintenanceScheduleChartAttribute.maintenanceScheduleType])

    const childChartData = useMemo(() => {
        if (!maintenanceScheduleForStation) return null;
        return getChildChartData(maintenanceScheduleForStation, maintenanceScheduleChartAttribute.maintenanceScheduleType || "");
    }, [maintenanceScheduleForStation, maintenanceScheduleChartAttribute.maintenanceScheduleType]);

    return (
        <>
            <div className="maintenance-schedule-widget">
                <div className="maintenance-schedule-widget-body">
                    <div className="maintenance-schedule-widget-body-header">
                        <div className="maintenance-schedule-widget-body-header-title">
                            <span className="maintenance-schedule-widget-body-header-title-text">Maintenance Schedule Status</span>
                            <div className="flex alignCenter gap-1">(
                                <span className="maintenance-schedule-widget-body-header-title-text to-be-started-text">To be started: {maintenanceScheduleStats.toBeStarted} </span> |
                                <span className="maintenance-schedule-widget-body-header-title-text ontime-completed-text">Ontime completed: {maintenanceScheduleStats.ontimeCompleted} </span> |
                                <span className="maintenance-schedule-widget-body-header-title-text overdue-completed-text">Overdue completed: {maintenanceScheduleStats.overdueCompleted} </span> |
                                <span className="maintenance-schedule-widget-body-header-title-text pending-overdue-text">Pending (Overdue): {maintenanceScheduleStats.pending} </span>
                                )</div>
                        </div>
                    </div>
                    <div style={{height: "480px"}}>
                        {mainChartData &&
                            <Chart
                                className="type-status-count-chart cursor-pointer"
                                type="bar"
                                options={mainChartOptions}
                                data={mainChartData}
                            />
                        }
                    </div>
                </div>
            </div>

            {(maintenanceScheduleForStation && childChartData) &&
                <div className="maintenance-schedule-widget child-chart-container" ref={childChartRef}>
                    <div className="maintenance-schedule-widget-body">
                        <div className="maintenance-schedule-widget-body-header">
                            <div className="maintenance-schedule-widget-body-header-title">
                                <span className="maintenance-schedule-widget-body-header-title-text" style={{color: colors[maintenanceScheduleChartAttribute.maintenanceScheduleType as keyof typeof colors]}}>
                                    {/* {assetChartAttribute.station ? `Asset Details for ${assetChartAttribute.station}` : 'Select a station to view details'} */}
                                    {maintenanceScheduleChartAttribute.station} - {getMaintenanceScheduleTypeLabel(maintenanceScheduleChartAttribute.maintenanceScheduleType || '')}
                                </span>
                            </div>
                        </div>
                        <div style={{height: "480px"}}>
                            <Chart
                                className="type-status-count-chart cursor-pointer"
                                type="bar"
                                options={childChartOptions}
                                data={childChartData}
                            />
                        </div>
                    </div>
                </div>
            }
        </>
    )
}
export default MaintenanceScheduleWidget;